import { useState } from 'react';
import { Settings, Plus, Trash2, FastForward, Zap, Users, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
// Using native checkbox toggle - no shadcn Switch component available
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/ui/empty-state';
import { EventSelector } from '@/components/delegate/EventSelector';
import {
  useRoundConfigs,
  useUpsertRoundConfig,
  useDeleteRoundConfig,
  useSetupShortRound,
  useShortRound,
  useAdvanceTeams,
  useUpdateTeamStatus,
  useDeleteTeam,
  useBatchRecalculate,
} from '@/api/extensions/useScoringOps';
import { useDrawTeams } from '@/api/extensions/useDrawOps';
import { useEventDetail } from '@/api/extensions/useRodeoEvents';
import { resolveEventScoring, timedScoreDecimals } from '@/constants/event-types';
import type { RoundConfig, ShortRoundTeam } from '@/api/extensions/useScoringOps';

export default function DelegateManageScoringPage() {
  const [eventId, setEventId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Settings className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-2xl font-bold">Manage Scoring</h1>
      </div>

      <EventSelector
        onEventSelect={setEventId}
        selectedEventId={eventId}
      />

      {eventId && (
        <>
          <Separator />
          <RoundConfigSection eventId={eventId} />
          <ShortRoundSection eventId={eventId} />
          <AdvanceTeamsSection eventId={eventId} />
          <TeamStatusSection eventId={eventId} />
          <RecalculateSection eventId={eventId} />
        </>
      )}
    </div>
  );
}

// ─── Round Configuration ────────────────────────────────────────

function RoundConfigSection({ eventId }: { eventId: string }) {
  const { data: configsResponse, isLoading } = useRoundConfigs(eventId);
  const upsert = useUpsertRoundConfig();
  const deleteConfig = useDeleteRoundConfig();

  const [showAdd, setShowAdd] = useState(false);
  const [newRound, setNewRound] = useState({
    round_number: 1,
    is_progressive: false,
    is_short_go: false,
    advancing_count: '',
    short_go_order: 'slowest_first' as 'slowest_first' | 'fastest_first',
  });
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  const roundConfigs = (configsResponse as any)?.data ?? (configsResponse as unknown as any[]) ?? [];

  const handleAdd = async () => {
    await upsert.mutateAsync({
      eventId,
      roundNumber: newRound.round_number,
      is_progressive: newRound.is_progressive,
      is_short_go: newRound.is_short_go,
      advancing_count: newRound.advancing_count ? parseInt(newRound.advancing_count) : undefined,
      short_go_order: newRound.is_short_go ? newRound.short_go_order : undefined,
    });
    setShowAdd(false);
    setNewRound({
      round_number: roundConfigs.length + 2,
      is_progressive: false,
      is_short_go: false,
      advancing_count: '',
      short_go_order: 'slowest_first',
    });
  };

  const handleDelete = async () => {
    if (deleteTarget == null) return;
    await deleteConfig.mutateAsync({ eventId, roundNumber: deleteTarget });
    setDeleteTarget(null);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          Round Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : roundConfigs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No rounds configured.</p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Round</TableHead>
                  <TableHead>Progressive</TableHead>
                  <TableHead>Short Go</TableHead>
                  <TableHead>Advancing</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {roundConfigs.map((rc: RoundConfig) => (
                  <TableRow key={rc.round_number ?? rc.id}>
                    <TableCell className="font-medium text-sm">
                      Round {rc.round_number}
                    </TableCell>
                    <TableCell>
                      {rc.is_progressive ? (
                        <Badge variant="default" className="text-xs">Yes</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">No</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {rc.is_short_go ? (
                        <Badge className="text-xs bg-amber-600">Short Go</Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {rc.advancing_count ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => setDeleteTarget(rc.round_number)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {showAdd ? (
          <div className="border rounded-md p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Round #</Label>
                <Input
                  type="number"
                  min={1}
                  value={newRound.round_number}
                  onChange={(e) =>
                    setNewRound({ ...newRound, round_number: parseInt(e.target.value) || 1 })
                  }
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Advancing Count</Label>
                <Input
                  type="number"
                  min={1}
                  max={25}
                  value={newRound.advancing_count}
                  onChange={(e) => setNewRound({ ...newRound, advancing_count: e.target.value })}
                  placeholder="e.g. 10"
                  className="h-8 text-sm"
                  disabled={!newRound.is_progressive}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newRound.is_progressive}
                  onChange={(e) => setNewRound({ ...newRound, is_progressive: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label className="text-xs">Progressive</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newRound.is_short_go}
                  onChange={(e) => setNewRound({ ...newRound, is_short_go: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label className="text-xs">Short Go</Label>
              </div>
              {newRound.is_short_go && (
                <div className="space-y-1">
                  <Label className="text-xs">Order</Label>
                  <Select
                    value={newRound.short_go_order}
                    onValueChange={(v) => setNewRound({ ...newRound, short_go_order: v as any })}
                  >
                    <SelectTrigger className="h-8 text-sm w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="slowest_first">Slowest First</SelectItem>
                      <SelectItem value="fastest_first">Fastest First</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} disabled={upsert.isPending}>
                {upsert.isPending ? 'Saving...' : 'Add Round'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => {
            setNewRound({
              round_number: roundConfigs.length + 1,
              is_progressive: false,
              is_short_go: false,
              advancing_count: '',
              short_go_order: 'slowest_first',
            });
            setShowAdd(true);
          }}>
            <Plus className="h-4 w-4 mr-1" /> Add Round
          </Button>
        )}

        {/* Delete confirmation */}
        <Dialog open={deleteTarget != null} onOpenChange={() => setDeleteTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Round {deleteTarget}</DialogTitle>
              <DialogDescription>
                Remove this round configuration? Existing scores for this round will be orphaned.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleteConfig.isPending}>
                {deleteConfig.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ─── Short Round ────────────────────────────────────────────────

function ShortRoundSection({ eventId }: { eventId: string }) {
  const [showSetup, setShowSetup] = useState(false);
  const [order, setOrder] = useState<'slowest_first' | 'fastest_first'>('slowest_first');

  const { data: qualifyingResponse, isLoading } = useShortRound(eventId);
  const { data: eventDetail } = useEventDetail(eventId);
  const setup = useSetupShortRound();

  const qualifyingTeams = (qualifyingResponse as any)?.data ?? (qualifyingResponse as unknown as any[]) ?? [];
  // Barrel racing times show to the thousandth; other timed events to hundredths.
  const timeDecimals = timedScoreDecimals(resolveEventScoring(eventDetail as any).isBarrelRacing);

  const handleSetup = async () => {
    await setup.mutateAsync({
      eventId,
      order,
    });
    setShowSetup(false);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Zap className="h-4 w-4" /> Short Round
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : qualifyingTeams.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pos</TableHead>
                  <TableHead>Team #</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead className="text-right">Total Time</TableHead>
                  <TableHead className="text-right">Catches</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {qualifyingTeams.map((t: ShortRoundTeam) => (
                  <TableRow key={t.team_id}>
                    <TableCell className="font-mono text-sm">{t.short_round_position ?? '—'}</TableCell>
                    <TableCell className="font-mono text-sm">{t.team_number}</TableCell>
                    <TableCell className="text-sm">
                      {t.member_names?.join(' / ') ?? '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {t.total_time != null ? `${Number(t.total_time).toFixed(timeDecimals)}s` : 'NT'}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {t.successful_catches ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No short round configured.</p>
        )}

        {showSetup ? (
          <div className="border rounded-md p-3 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Sort Order</Label>
              <Select value={order} onValueChange={(v) => setOrder(v as any)}>
                <SelectTrigger className="h-8 text-sm w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="slowest_first">Slowest First</SelectItem>
                  <SelectItem value="fastest_first">Fastest First</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Active teams will be ordered by total time and assigned short round positions.
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSetup} disabled={setup.isPending}>
                {setup.isPending ? 'Setting up...' : 'Setup Short Round'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowSetup(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setShowSetup(true)}>
            <Zap className="h-4 w-4 mr-1" /> Setup Short Round
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Advance Teams ──────────────────────────────────────────────

function AdvanceTeamsSection({ eventId }: { eventId: string }) {
  const [showAdvance, setShowAdvance] = useState(false);
  const [overrideCount, setOverrideCount] = useState('');
  const [roundNumber, setRoundNumber] = useState('1');
  const [result, setResult] = useState<any>(null);

  const advance = useAdvanceTeams();

  const handleAdvance = async () => {
    const res = await advance.mutateAsync({
      eventId,
      roundNumber: parseInt(roundNumber) || 1,
      override_count: overrideCount ? parseInt(overrideCount) : undefined,
    });
    setResult(res);
    setShowAdvance(false);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <FastForward className="h-4 w-4" /> Advance Teams
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {result && (
          <div className="text-sm p-2 rounded-md bg-muted">
            <p>Advanced: <strong>{result.advancing_count ?? 0}</strong> teams</p>
            <p>Eliminated: <strong>{result.eliminated_count ?? 0}</strong> teams</p>
          </div>
        )}

        {showAdvance ? (
          <div className="border rounded-md p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">From Round #</Label>
                <Input
                  type="number"
                  min={1}
                  value={roundNumber}
                  onChange={(e) => setRoundNumber(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Override Count (blank = config)</Label>
                <Input
                  type="number"
                  min={1}
                  max={25}
                  value={overrideCount}
                  onChange={(e) => setOverrideCount(e.target.value)}
                  placeholder="From round config"
                  className="h-8 text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdvance} disabled={advance.isPending}>
                {advance.isPending ? 'Advancing...' : 'Advance Teams'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdvance(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setShowAdvance(true)}>
            <FastForward className="h-4 w-4 mr-1" /> Advance Teams from Round
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Team Status Management ─────────────────────────────────────

function TeamStatusSection({ eventId }: { eventId: string }) {
  const { data: teams, isLoading } = useDrawTeams(eventId);
  const updateStatus = useUpdateTeamStatus();
  const deleteTeam = useDeleteTeam();
  const [editingTeam, setEditingTeam] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const teamList = (teams as any)?.data ?? (teams as any[]) ?? [];

  const handleUpdate = async (teamId: string) => {
    if (!newStatus) return;
    await updateStatus.mutateAsync({
      eventId,
      teamId,
      status: newStatus,
    });
    setEditingTeam(null);
    setNewStatus('');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteTeam.mutateAsync({ eventId, teamId: deleteTarget });
    setDeleteTarget(null);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Users className="h-4 w-4" /> Team Status
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : teamList.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No teams"
            description="Execute the draw first to see teams here."
          />
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team #</TableHead>
                  <TableHead>Header</TableHead>
                  <TableHead>Heeler</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-40" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamList.map((team: any) => {
                  const names = team.member_names ?? [];
                  return (
                    <TableRow key={team.id}>
                      <TableCell className="font-mono text-sm">{team.team_number}</TableCell>
                      <TableCell className="text-sm">{names[0] ?? team.header_name ?? '—'}</TableCell>
                      <TableCell className="text-sm">{names[1] ?? team.heeler_name ?? '—'}</TableCell>
                      <TableCell>
                        {editingTeam === team.id ? (
                          <Select value={newStatus} onValueChange={setNewStatus}>
                            <SelectTrigger className="h-7 w-28 text-xs">
                              <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="scratched">Scratched</SelectItem>
                              <SelectItem value="eliminated">Eliminated</SelectItem>
                              <SelectItem value="disqualified">Disqualified</SelectItem>
                              <SelectItem value="turned_out">Turned Out</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="secondary" className="text-xs capitalize">
                            {(team.status ?? 'active').replace('_', ' ')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingTeam === team.id ? (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => handleUpdate(team.id)}
                              disabled={updateStatus.isPending || !newStatus}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs"
                              onClick={() => setEditingTeam(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs"
                              onClick={() => {
                                setEditingTeam(team.id);
                                setNewStatus(team.status ?? 'active');
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs text-destructive"
                              onClick={() => setDeleteTarget(team.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Delete team confirmation */}
        <Dialog open={deleteTarget != null} onOpenChange={() => setDeleteTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Team</DialogTitle>
              <DialogDescription>
                This will permanently delete the team, its members, and all performance records. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleteTeam.isPending}>
                {deleteTeam.isPending ? 'Deleting...' : 'Delete Team'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ─── Batch Recalculate ──────────────────────────────────────────

function RecalculateSection({ eventId }: { eventId: string }) {
  const recalculate = useBatchRecalculate();
  const [result, setResult] = useState<any>(null);

  const handleRecalculate = async () => {
    const res = await recalculate.mutateAsync({ eventId });
    setResult(res);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> Recalculate Scores
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Recalculate all final times using current slide/handicap settings. Use after changing event configuration.
        </p>
        {result && (
          <div className="text-sm p-2 rounded-md bg-muted">
            <p>Recalculated: <strong>{result.recalculated ?? 0}</strong> scores</p>
            <p>Teams updated: <strong>{result.teams_updated ?? 0}</strong></p>
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={handleRecalculate}
          disabled={recalculate.isPending}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${recalculate.isPending ? 'animate-spin' : ''}`} />
          {recalculate.isPending ? 'Recalculating...' : 'Recalculate All Scores'}
        </Button>
      </CardContent>
    </Card>
  );
}
